import React, { useRef, useEffect, useState, useCallback } from "react";
import Webcam from "react-webcam";

export default function StepPhoto({ images, setImages, nextStep }) {
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]); // For storing video chunks
  const fileInputRef = useRef(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [bgRemoved, setBgRemoved] = useState(false);

  // State for handling video (slot 3)
  const [isRecording, setIsRecording] = useState(false);
  // User option: include audio when recording video
  const [includeAudio, setIncludeAudio] = useState(false);

  // currentIndex:
  // 0 y 1 → fotos con edición (opción para remover fondo);
  // 2 → foto sin edición;
  // 3 → video.
  const [currentIndex, setCurrentIndex] = useState(0);

  // Al montar el componente, se busca el primer slot vacío.
  // Se elimina "images" de las dependencias para que no cambie automáticamente al capturar.
  useEffect(() => {
    const nextEmptySlot = images.findIndex((img) => !img);
    if (nextEmptySlot !== -1) {
      setCurrentIndex(nextEmptySlot);
    } else {
      // Si todos los slots están llenos, se queda en el último (video)
      setCurrentIndex(images.length - 1);
    }
  }, []); // <-- Ejecuta solo una vez al montar

  // Escucha eventos de apertura/cierre del modal para iniciar/detener la cámara.
  useEffect(() => {
    const modal = document.getElementById("my_modal_5");
    if (!modal) return;

    const checkModalOpen = () => {
      if (modal.open && !cameraActive) {
        startCamera();
      } else if (!modal.open && cameraActive) {
        stopCamera();
      }
    };

    const observer = new MutationObserver(() => {
      checkModalOpen();
    });

    observer.observe(modal, { attributes: true, attributeFilter: ["open"] });
    checkModalOpen();

    return () => {
      observer.disconnect();
    };
  }, [cameraActive]);

  // Cuando se está en modo video, si cambia includeAudio se reinicia la cámara.
  useEffect(() => {
    if (currentIndex === 3 && cameraActive) {
      stopCamera();
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAudio, currentIndex]);

  const startCamera = async () => {
    try {
      if (streamRef.current) {
        stopCamera();
      }
      const constraints = {
        video: { facingMode: "environment" },
        audio: currentIndex === 3 ? includeAudio : false,
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;
      if (webcamRef.current && streamRef.current) {
        webcamRef.current.video.srcObject = streamRef.current;
      }
      setCameraActive(true);
    } catch (err) {
      console.error("Error starting the camera:", err);
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  // Captura foto (para slots 0, 1 y 2)
  const capturePhoto = () => {
    if (!webcamRef.current) return;
    const video = webcamRef.current.video;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const photo = canvas.toDataURL("image/jpeg", 1.0);
    const newImages = [...images];
    newImages[currentIndex] = photo;
    setImages(newImages);
    setBgRemoved(false);
  };

  // Maneja la carga de archivos para el slot 2
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const photo = reader.result;
      const newImages = [...images];
      newImages[currentIndex] = photo;
      setImages(newImages);
    };
    reader.readAsDataURL(file);
  };

  // Maneja los datos para grabar video
  const handleDataAvailable = useCallback((event) => {
    if (event.data.size > 0) {
      recordedChunksRef.current.push(event.data);
    }
  }, []);

  const startRecording = useCallback(() => {
    recordedChunksRef.current = [];
    if (streamRef.current) {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, {
        mimeType: "video/webm",
      });
      mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
      mediaRecorderRef.current.onstop = () => {
        if (currentIndex === 3 && recordedChunksRef.current.length > 0) {
          const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
          const videoUrl = URL.createObjectURL(blob);
          setImages((prevImages) => {
            const newImages = [...prevImages];
            newImages[currentIndex] = videoUrl;
            return newImages;
          });
        }
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
    }
  }, [handleDataAvailable, currentIndex, setImages]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  // Remueve el fondo (solo para índices 0 y 1)
  const removeBackground = async () => {
    if (currentIndex > 1) return;
    const currentPhoto = images[currentIndex];
    if (!currentPhoto) return;
    setProcessing(true);
    try {
      const formData = new FormData();
      const res = await fetch(currentPhoto);
      const blob = await res.blob();
      const API_KEY = process.env.REACT_APP_PHOTOROOM_API_KEY;
      formData.append("image_file", blob, "image.jpg");

      const response = await fetch("https://sdk.photoroom.com/v1/segment", {
        method: "POST",
        headers: {
          Accept: "image/png, application/json",
          "x-api-key": API_KEY,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Error removing background");
      }

      const resultBlob = await response.blob();
      const newImageUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(resultBlob);
      });

      const newImages = [...images];
      newImages[currentIndex] = newImageUrl;
      setImages(newImages);
      setBgRemoved(true);
    } catch (error) {
      console.error("Error removing background:", error);
    } finally {
      setProcessing(false);
    }
  };

  // Navegación entre slots de media.
  const handleNextMedia = () => {
    if (currentIndex < 3) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevMedia = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const retakeCurrentMedia = () => {
    const newImages = [...images];
    newImages[currentIndex] = null;
    setImages(newImages);
    setBgRemoved(false);
    recordedChunksRef.current = [];
  };

  const handleNextStep = () => {
    stopCamera();
    nextStep();
  };

  const handleCancel = () => {
    stopCamera();
    setImages([null, null, null, null]);
    document.getElementById("my_modal_5").close();
  };

  const getMediaLabel = () => {
    switch (currentIndex) {
      case 0:
        return "First Image";
      case 1:
        return "Second Image";
      case 2:
        return "Third Image";
      case 3:
        return "Video";
      default:
        return "";
    }
  };

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-xl font-bold mb-4">Capture up to 4 media items</h2>
      <div className="flex-grow flex flex-col items-center justify-center px-4">
        {/* Contenedor con altura fija para la cámara o vista previa */}
        {!images[currentIndex] && cameraActive ? (
          <>
            <div className="w-full h-64 flex items-center justify-center">
              <Webcam
                audio={currentIndex === 3 ? includeAudio : false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "environment" }}
                className="rounded-md w-full h-full object-cover"
              />
            </div>
            {currentIndex === 3 ? (
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className="btn text-black w-full mt-2"
              >
                {isRecording ? "Stop Recording" : "Start Recording"}
              </button>
            ) : (
              <>
                {currentIndex === 2 ? (
                  <>
                    <button
                      onClick={capturePhoto}
                      className="btn text-black w-full mt-2"
                    >
                      Take Photo (No Editing)
                    </button>
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="btn text-black w-full mt-2"
                    >
                      Upload Photo
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      style={{ display: "none" }}
                    />
                  </>
                ) : (
                  <button
                    onClick={capturePhoto}
                    className="btn text-black w-full mt-2"
                  >
                    {`Take Photo ${currentIndex + 1}`}
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          // Vista previa y opciones cuando se ha capturado la media
          images[currentIndex] && (
            <>
              {currentIndex !== 3 ? (
                <>
                  <div className="w-full h-64 flex items-center justify-center">
                    <img
                      src={images[currentIndex]}
                      alt={`Photo ${currentIndex + 1}`}
                      className="rounded-md w-full h-full object-contain"
                      style={{ backgroundColor: "white" }}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    {currentIndex < 2 && (
                      <button
                        onClick={removeBackground}
                        disabled={processing}
                        className="btn text-black flex-1"
                      >
                        {processing ? "Removing..." : "Remove Background"}
                      </button>
                    )}
                    {currentIndex === 2 && (
                      <>
                        <button
                          onClick={() => fileInputRef.current.click()}
                          className="btn text-black flex-1"
                        >
                          Upload Photo
                        </button>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          style={{ display: "none" }}
                        />
                      </>
                    )}
                    <button
                      onClick={retakeCurrentMedia}
                      className="btn text-black flex-1"
                    >
                      Retake {currentIndex === 3 ? "Video" : "Photo"}
                    </button>
                  </div>
                </>
              ) : (
                // Vista previa de video (slot 3)
                <>
                  <div className="w-full h-64 flex items-center justify-center">
                    <video
                      src={images[currentIndex]}
                      controls
                      className="rounded-md w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={retakeCurrentMedia}
                      className="btn text-black flex-1"
                    >
                      Retake Video
                    </button>
                  </div>
                </>
              )}
            </>
          )
        )}
      </div>
      <div className="text-center text-lg font-semibold mt-2">
        {getMediaLabel()}
      </div>
      {/* Navegación entre slots */}
      <div className="flex justify-center gap-2 mb-4 mt-2">
        <button
          onClick={handlePrevMedia}
          disabled={currentIndex === 0}
          className="btn btn-sm"
        >
          Previous
        </button>
        <button
          onClick={handleNextMedia}
          disabled={currentIndex === 3}
          className="btn btn-sm"
        >
          Next
        </button>
      </div>
      {/* Vista previa de miniaturas */}
      <div className="flex justify-center gap-2 mb-2">
        {images.map((media, i) => (
          <div
            key={i}
            className="w-16 h-16 bg-gray-200 flex items-center justify-center border"
          >
            {media ? (
              i === 3 ? (
                <video src={media} className="object-cover w-full h-full" />
              ) : (
                <img
                  src={media}
                  alt={`Media ${i + 1}`}
                  className="object-cover w-full h-full"
                />
              )
            ) : (
              <span className="text-xs text-gray-500">Empty</span>
            )}
          </div>
        ))}
      </div>
      <div className="text-sm text-gray-500">
        <p>
          <strong>Note:</strong> You can capture up to 4 media items. The first two will be photos with the option to remove the background and edit; the third without editing (where you can take or upload a photo) and the fourth is a video.
        </p>
      </div>
      <div className="modal-action px-4 py-3 border-t flex justify-between items-center gap-2 w-full">
        <button
          onClick={handleCancel}
          className="btn btn-outline text-xs sm:text-sm px-3 sm:px-4 w-auto max-w-[100px]"
        >
          Cancel
        </button>
        <button
          onClick={handleNextStep}
          disabled={!images[0]}
          className={`btn ${
            images[0]
              ? "bg-green-500 hover:bg-green-600 text-white"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
